import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTimezone } from '../contexts/TimezoneContext';
import type { User } from '../types';
import { profileService } from '../services/profile.service';
import type { UpdateProfileDto } from '../services/profile.service';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { formatDateTime } from '../utils/dateUtils';
import { getUploadsBaseURL } from '../utils/apiConfig';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';

export default function Profile() {
  const { user, updateUser } = useAuth();
  const { timezone, timezones, updateTimezone, refreshTimezone } = useTimezone();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'profile' | 'settings'>('profile');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Formulário de perfil - inicializar com user.name se disponível
  const [profileData, setProfileData] = useState<{ name: string }>(() => ({
    name: user?.name || ''
  }));

  // Estado local para timezone
  const [selectedTimezone, setSelectedTimezone] = useState(timezone || 'America/Sao_Paulo');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Preview derivado de timezone
  const previewTime = selectedTimezone ? formatDateTime(new Date(), selectedTimezone) : '';

  // Sincronizar profileData.name com user.name quando user.name mudar
  useEffect(() => {
    if (user?.name && profileData.name !== user.name) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProfileData(prev => ({ ...prev, name: user.name }));
    }
  }, [user?.name, profileData.name]);

  // Mutation para atualizar perfil
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileDto) => profileService.updateProfile(data),
    onSuccess: (data) => {
      setSuccessMessage('Perfil atualizado com sucesso!');
      setErrorMessage('');
      // Atualizar dados do usuário no estado e localStorage
      updateUser({ name: data.name, avatar: data.avatar, imagem: data.avatar });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(err.response?.data?.message || 'Erro ao atualizar perfil');
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    },
  });

  // Mutation para upload de avatar
  const uploadAvatarMutation = useMutation({
    mutationFn: (file: File) => profileService.uploadAvatar(file),
    onSuccess: (data) => {
      console.log('✅ [Profile] Avatar upload success:', data);
      setSuccessMessage('Foto atualizada com sucesso!');
      setErrorMessage('');
      // Atualizar dados do usuário no estado e localStorage
      const avatarPath = data.avatar;
      console.log('🔄 [Profile] Updating user with avatar:', avatarPath);
      updateUser({ avatar: avatarPath, imagem: avatarPath });
      // Forçar atualização do estado local também
      setProfileData(prev => ({ ...prev }));
      queryClient.invalidateQueries({ queryKey: ['user'] });
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(err.response?.data?.message || 'Erro ao fazer upload da foto');
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    },
  });

  // Mutation para atualizar timezone
  const updateTimezoneMutation = useMutation({
    mutationFn: async (newTimezone: string) => {
      if (!user) throw new Error('Usuário não encontrado');
      // updateTimezone do contexto já usa o endpoint /me/timezone
      await updateTimezone(newTimezone);
    },
    onSuccess: (_, newTimezone) => {
      setSuccessMessage('Timezone atualizado com sucesso!');
      setErrorMessage('');
      refreshTimezone();
      updateUser({ timezone: newTimezone });
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(err.response?.data?.message || 'Erro ao atualizar timezone');
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    updateProfileMutation.mutate(profileData);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP');
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Arquivo muito grande. Tamanho máximo: 5MB');
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    uploadAvatarMutation.mutate(file);
  };

  const getAvatarUrl = () => {
    const avatar = displayUser?.avatar || displayUser?.imagem;
    console.log('🖼️ [Profile] getAvatarUrl, displayUser:', displayUser, 'avatar:', avatar);
    if (avatar) {
      // Se for caminho relativo, construir URL completa
      if (avatar.startsWith('avatars/')) {
        const url = `${getUploadsBaseURL()}/${avatar}`;
        console.log('🖼️ [Profile] Avatar URL (relative):', url);
        return url;
      }
      if (avatar.startsWith('http')) {
        console.log('🖼️ [Profile] Avatar URL (absolute):', avatar);
        return avatar;
      }
      const url = `${getUploadsBaseURL()}/${avatar}`;
      console.log('🖼️ [Profile] Avatar URL (default):', url);
      return url;
    }
    console.log('🖼️ [Profile] No avatar found');
    return null;
  };

  // Usuário padrão caso não esteja carregado
  const displayUser: User = user || { id: '0', name: 'Carregando...', email: '' };
  
  // Log para debug
  console.log('👤 [Profile] displayUser:', displayUser);
  console.log('👤 [Profile] user from useAuth:', user);
  
  // Forçar re-render quando o avatar mudar
  useEffect(() => {
    console.log('🔄 [Profile] User changed, avatar:', user?.avatar || user?.imagem);
  }, [user?.avatar, user?.imagem]);

  if (!user) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Carregando perfil...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Meu Perfil"
        description="Gerencie suas informações de conta e preferências"
      />

        {/* Mensagens de sucesso/erro */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <XCircleIcon className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{errorMessage}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'profile'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Perfil
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'settings'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Configurações
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Tab: Perfil */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                {/* Upload de Foto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto de Perfil
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {getAvatarUrl() ? (
                        <img
                          key={displayUser?.avatar || displayUser?.imagem || 'no-avatar'}
                          src={getAvatarUrl() || undefined}
                          alt="Avatar"
                          className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
                          onError={(e) => {
                            console.error('❌ [Profile] Error loading avatar:', getAvatarUrl());
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ [Profile] Avatar loaded successfully:', getAvatarUrl());
                          }}
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                          <span className="text-2xl text-gray-500">
                            {(displayUser.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <label
                        htmlFor="avatar-upload"
                        className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors"
                        title="Alterar foto"
                      >
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </label>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleAvatarChange}
                        className="hidden"
                        disabled={uploadAvatarMutation.isPending}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">
                        Clique na câmera para alterar sua foto de perfil
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Formatos aceitos: JPEG, PNG, GIF, WebP. Tamanho máximo: 5MB
                      </p>
                      {uploadAvatarMutation.isPending && (
                        <p className="text-sm text-primary-600 mt-2">Enviando foto...</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nome de Exibição
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-sm text-gray-500">O e-mail não pode ser alterado</p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isPending}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updateProfileMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            )}

            {/* Tab: Configurações */}
            {activeTab === 'settings' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (selectedTimezone && selectedTimezone !== timezone) {
                    setShowConfirmModal(true);
                  } else if (selectedTimezone) {
                    updateTimezoneMutation.mutate(selectedTimezone);
                  }
                }}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                    Fuso Horário
                  </label>
                  <select
                    id="timezone"
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={updateTimezoneMutation.isPending}
                  >
                    {timezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Selecione o fuso horário da sua região para exibir as datas e horas corretamente
                  </p>
                </div>

                {/* Preview do horário */}
                {selectedTimezone && selectedTimezone !== timezone && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Preview do horário no novo timezone:
                    </p>
                    <p className="text-lg font-semibold text-blue-700">
                      {previewTime}
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      Horário atual no timezone selecionado
                    </p>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateTimezoneMutation.isPending || selectedTimezone === timezone}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updateTimezoneMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
                  </button>
                </div>
              </form>
            )}

            {/* Modal de Confirmação */}
            {showConfirmModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Confirmar alteração de timezone
                    </h3>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">
                        Você está alterando o timezone de:
                      </p>
                      <p className="font-medium text-gray-900">
                        {timezones.find(tz => tz.value === timezone)?.label || timezone}
                      </p>
                      <p className="text-sm text-gray-600 mt-3 mb-2">
                        Para:
                      </p>
                      <p className="font-medium text-gray-900">
                        {timezones.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
                      </p>
                      {previewTime && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                          <p className="text-xs text-blue-600 mb-1">Horário atual no novo timezone:</p>
                          <p className="text-sm font-semibold text-blue-900">{previewTime}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowConfirmModal(false);
                          setSelectedTimezone(timezone); // Reverter para o timezone atual
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowConfirmModal(false);
                          if (selectedTimezone) {
                            updateTimezoneMutation.mutate(selectedTimezone);
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
    </PageContainer>
  );
}



